/**
 * TypeScript types for the server-driven paywall config (#251, Milestone 13
 * Phase 1 foundation).
 *
 * Invariant: the client NEVER sends price or trial length — it only ever
 * receives a pre-approved `stripe_price_id` and reads `trial_days` from this
 * config. Never hardcode a price or trial duration in the client.
 */

export type PaywallContext = 'owner' | 'driver_self_pay';

/**
 * Product chosen in the chat (WS6/WS6b). One per purchase — other
 * combinations stay in the dashboard. Only 'hours' has a trial; GPS/combo
 * carry a device setup fee and are charged upfront.
 */
export type PaywallFeature = 'hours' | 'gps' | 'combo';

export const PAYWALL_FUNNEL = 'm13_registration_paywall' as const;

export type D0Behavior = 'block';

export interface PaywallPlan {
  stripe_price_id: string;
  display_key: string;
  trial_days: number;
  requires_card: boolean;
}

export interface PaywallCopyKeys {
  timeline_title: string;
  banner: string;
  [key: string]: string;
}

export interface PaywallLayoutFlags {
  [key: string]: boolean | string | number;
}

export type PaywallPersona = 'owner' | 'driver';
export type BillingActor = 'company' | 'driver';
export type PurchaseIntent = 'company_licenses' | 'self_pay_license';

export interface PaywallTrackingMetadata {
  funnel: typeof PAYWALL_FUNNEL;
  feature?: PaywallFeature;
  persona: PaywallPersona;
  billing_actor: BillingActor;
  purchase_intent: PurchaseIntent;
  requires_company_context: boolean;
  requires_card_upfront: boolean;
  trial_days: number;
}

export interface PaywallConfig {
  context: PaywallContext;
  feature?: PaywallFeature;
  experiment: string;
  variant: string;
  plans: PaywallPlan[];
  copy_keys: PaywallCopyKeys;
  layout_flags: PaywallLayoutFlags;
  d0_behavior: D0Behavior;
  d3_channels: string[];
  company_grace_days: number;
  tracking: PaywallTrackingMetadata;
}

/**
 * Paywall state machine response (#209) — GET /api/subscriptions/status/.
 * Q8 grandfather: dates reflect the subscription's REAL trial_end.
 */
export type PaywallState =
  | 'no_subscription'
  | 'trialing'
  | 'active'
  | 'trial_expired'
  | 'company_grace'
  | 'blocked';

export interface PaywallStatusResponse {
  state: PaywallState;
  trial_ends_at: string | null;
  days_remaining: number | null;
  trial_days: number | null;
  company_grace_ends_at: string | null;
  grace_days_remaining: number | null;
  is_owner: boolean;
  is_self_pay: boolean;
  payment_action_required: boolean;
}
